import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import DataConnector from 'connect/DataConnector';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  requireScientificModel,
  type ScientificModelNode,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {InProgressPredictionData} from 'model-hub/scientific-models/structure-prediction/CoordinateStructureList';
import StructurePredictionTab from 'model-hub/scientific-models/structure-prediction/StructurePredictionTab';
import {ExternalFileStructureInfo} from 'model-hub/scientific-models/structure-prediction/StructurePredictionUtils';
import {ViewState} from 'model-hub/scientific-models/structure-prediction/structurePredictionViewState';
import {StructureViewerPane} from 'model-hub/scientific-models/structure-prediction/StructureViewerPane';
import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {ProteinVersion} from 'molbio/aa-sequences/util/AaSequenceConstants';
import notifications from 'notifications';
import {aaSequenceSelectionUIGlobalState} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import legacyRunModal from 'ui-library/legacyRunModal';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  HasInProgressStructurePredictionJobsQuery,
  HasInProgressStructurePredictionJobsQueryVariables,
} from './__generated/HasInProgressStructurePredictionJobsQuery';
import {
  findCoordinateStructureById,
  getStructuresForVersion,
  getStructureInfoFromVersions,
} from './AlphaFoldTabUtils';
import RedoPredictionModal from './RedoPredictionModal';

export const HAS_IN_PROGRESS_STRUCTURE_PREDICTION_JOBS: DocumentNode = gql`
  query HasInProgressStructurePredictionJobsQuery($proteinId: StrictString!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    aaSequence(id: $proteinId) {
      ... on AaSequenceNode {
        id
        inProgressStructurePredictionJobs {
          id
          modelName
          createdAt
          componentApiIds
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type AlphaFoldTabContentProps =
  | {
      aaSequenceId: string;
      initialView?: undefined;
      clearInitialView?: undefined;
    }
  | {
      aaSequenceId: string;
      initialView: ViewState;
      clearInitialView: () => void;
    };

interface AlphaFoldTabContentEnhancedProps {
  versions?: Array<ProteinVersion>;
  currentlyViewedAaSequenceVersionId: number;
  aminoAcids: string;
  permissions: Permissions;
  currentCoordinateStructureId: string | null;
  folderId: string;
}

class AlphaFoldTabContentConnector extends DataConnector<
  AlphaFoldTabContentProps,
  AlphaFoldTabContentEnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceStore];

  enhanceProps(): AlphaFoldTabContentEnhancedProps {
    const aaSequenceStore = this.aaSequenceStore();
    const aaSequence = aaSequenceStore.getAaSequenceById(this.props.aaSequenceId)!;
    const currentlyViewedAaSequenceVersionId = aaSequence.version.id;
    return {
      versions: aaSequenceStore.getVersionsById(this.props.aaSequenceId),
      currentlyViewedAaSequenceVersionId,
      aminoAcids: aaSequence.version.aminoAcids,
      permissions: aaSequence.permissions,
      currentCoordinateStructureId: aaSequenceStore.getCoordinateStructureId(
        currentlyViewedAaSequenceVersionId
      ),
      folderId: aaSequence.folder.api_identifier,
    };
  }
}

function AlphaFoldTab({...props}: AlphaFoldTabContentProps & AlphaFoldTabContentEnhancedProps): JSX.Element {
  assert(
    props.initialView == null || props.clearInitialView != null,
    'clearInitialView must be provided when initialView is set to prevent component from resetting to initial view on every re-render'
  );
  const result = useScientificModelRegistry();
  const registryError = result.type === 'error' ? result.error : null;
  useEffect(() => {
    if (registryError != null) {
      notifications.error('Error when loading scientific models. Please refresh the page.');
    }
  }, [registryError]);

  if (result.type === 'loading') {
    return <CenteredSpinner />;
  }
  if (result.type === 'error') {
    return <></>;
  }
  return (
    <LoadedAlphaFoldTab
      {...props}
      availableModels={result.registry.availableStructurePredictionModels}
      catalogByExternalReference={result.registry.catalogByExternalReference}
    />
  );
}

function LoadedAlphaFoldTab({
  availableModels,
  catalogByExternalReference,
  aaSequenceId,
  initialView,
  clearInitialView,
  versions,
  currentlyViewedAaSequenceVersionId,
  aminoAcids,
  permissions,
  currentCoordinateStructureId,
  folderId,
}: AlphaFoldTabContentProps &
  AlphaFoldTabContentEnhancedProps & {
    availableModels: ReadonlyArray<ScientificModelNode>;
    catalogByExternalReference: ScientificModelsByExternalReference;
  }): JSX.Element {
  const [aaSequenceSelectionUIState] = aaSequenceSelectionUIGlobalState.useGlobalClientState(aaSequenceId);
  const {start: selectionStart, end: selectionEnd} = aaSequenceSelectionUIState;
  const structureInfo = getStructureInfoFromVersions(currentlyViewedAaSequenceVersionId, versions);

  const structuresForVersion: Array<ExternalFileStructureInfo> = useMemo(
    () => getStructuresForVersion(versions, structureInfo.versionId || undefined),
    [versions, structureInfo.versionId]
  );

  // Use selected coordinate structure if available, otherwise fall back to structureInfo
  const selectedCoordinateStructure =
    (currentCoordinateStructureId
      ? findCoordinateStructureById(structuresForVersion, currentCoordinateStructureId)
      : null) ??
    (structureInfo.structureFile
      ? {
          preSigned: false,
          structureFile: structureInfo.structureFile,
          model: structureInfo.model,
          modelVersion: structureInfo.modelVersion,
          structureFileOutOfDate: structureInfo.structureFileOutOfDate || false,
          versionId: structureInfo.versionId,
          createdAt: structureInfo.createdAt,
          coordinateStructureId: structureInfo.coordinateStructureId,
          componentIds: structureInfo.componentIds,
        }
      : null);

  const batchQueryResult = useBenchlingQuery<
    HasInProgressStructurePredictionJobsQuery,
    HasInProgressStructurePredictionJobsQueryVariables
  >(HAS_IN_PROGRESS_STRUCTURE_PREDICTION_JOBS, {
    variables: {proteinId: aaSequenceId},
  });

  const refreshData = useCallback(() => {
    batchQueryResult.refetch();
  }, [batchQueryResult]);

  const batchQueryData = batchQueryResult.type === QueryResultType.SUCCESS ? batchQueryResult.data : null;

  const inProgressStructurePredictions: Array<InProgressPredictionData> = useMemo(() => {
    if (!batchQueryData || isInaccessible(batchQueryData.aaSequence)) {
      return [];
    }
    return (batchQueryData.aaSequence.inProgressStructurePredictionJobs ?? []).map((job) => ({
      id: job.id,
      model: requireScientificModel(catalogByExternalReference, job.modelName),
      createdAt: job.createdAt,
      componentIds: job.componentApiIds,
    }));
  }, [batchQueryData, catalogByExternalReference]);

  // Clear initialView from tab state after consuming it once
  useEffect(() => {
    if (initialView != null && clearInitialView) {
      clearInitialView();
    }
    // Only run once on mount when initialView is present
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear current coordinate structure when switching to coordinate structures list view
  const handleViewStateChange = useCallback(
    (viewState: ViewState): void => {
      if (viewState === ViewState.COORDINATE_STRUCTURES_LIST) {
        AaSequenceActions.setCoordinateStructureId(currentlyViewedAaSequenceVersionId, null);
      }
    },
    [currentlyViewedAaSequenceVersionId]
  );

  const onSelectCoordinateStructureId = useCallback(
    (coordinateStructureId: string): void => {
      AaSequenceActions.setCoordinateStructureId(currentlyViewedAaSequenceVersionId, coordinateStructureId);
    },
    [currentlyViewedAaSequenceVersionId]
  );

  return (
    <StructurePredictionTab
      availableModels={availableModels}
      structurePredictionQueryStatus={batchQueryResult.type}
      inProgressStructurePredictions={inProgressStructurePredictions}
      completedStructurePredictions={structuresForVersion}
      initialView={initialView}
      onViewStateChange={handleViewStateChange}
      permissions={permissions}
      catalogByExternalReference={catalogByExternalReference}
      onSelectCoordinateStructureId={onSelectCoordinateStructureId}
      onSubmitNewRun={refreshData}
      inputComponent={{id: aaSequenceId, folderId, permissions, aminoAcids}}
      versionId={currentlyViewedAaSequenceVersionId}
    >
      {({setViewState}) => {
        if (!selectedCoordinateStructure) {
          // Fallback to loading if somehow we get into an inconsistent state
          return <CenteredSpinner />;
        }
        const model = requireScientificModel(catalogByExternalReference, selectedCoordinateStructure.model);
        return (
          <StructureViewerPane
            structureInfo={selectedCoordinateStructure}
            catalogByExternalReference={catalogByExternalReference}
            predictionInProgress={inProgressStructurePredictions.length > 0}
            primaryEntityId={aaSequenceId}
            aminoAcids={aminoAcids}
            permissions={permissions}
            currentAaSequenceSequenceAminoAcids={aminoAcids}
            selectionStart={selectionStart}
            selectionEnd={selectionEnd}
            interactionAnalyticsEvent={MOLBIO_ANALYTICS_ACTIONS.INTERACTED_ALPHAFOLD_VIEWER}
            testComponent="AlphaFoldTabView"
            redoPrediction={() => {
              trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ALPHAFOLD_REDO_RUN);
              legacyRunModal(RedoPredictionModal, {
                availableModels,
                aaSequenceId,
                folderId,
                previouslyRanModel: model,
              });
            }}
            onBack={() => {
              setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
            }}
          />
        );
      }}
    </StructurePredictionTab>
  );
}

const ConnectedAlphaFoldContent = AlphaFoldTabContentConnector.connect(AlphaFoldTab);
export default ConnectedAlphaFoldContent;
